/**
 * InvoiceForm Component - Form for creating invoices
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
} from "@mui/material";
import { ArrowBack, Add, Delete, Save, Refresh } from "@mui/icons-material";
import {
  appendInvoiceItems,
  createInvoice,
  updateInvoice,
  getInvoice,
  loadQuoteForInvoice,
} from "../services/invoiceService.js";
import CustomerSearchAutocomplete from "../../../shared/components/CustomerSearchAutocomplete.jsx";
import OrderSearchAutocomplete from "../../../shared/components/OrderSearchAutocomplete.jsx";
import PriceCardLineItemEditor from "../../priceCard/components/PriceCardLineItemEditor.jsx";
import { resolveQuoteItemPricing } from "../utils/resolveQuoteItemPricing.js";
import { clearPriceCardSnapshot } from "../../priceCard/utils/lineItemPricing.js";
import SupplierSelect from "../../../shared/components/SupplierSelect.jsx";
import CreateProductModal from "../../products/components/CreateProductModal.jsx";

const createEmptyLineItem = () => ({
  product: null,
  productId: null,
  description: "",
  quantity: 1,
  unitPrice: "",
  total: 0,
  supplier: "bubakii",
  isCustom: true,
  marginMode: null,
  providerCostAmount: "",
  platformMarginAmount: "",
});

/**
 * InvoiceForm Component
 */
const InvoiceForm = ({ quoteData = null, onMissingTariff }) => {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const quoteIdParam = searchParams.get("quoteId");
  const jobIdParam = searchParams.get("jobId");
  const customerIdParam = searchParams.get("customerId");
  const isAppendMode = searchParams.get("append") === "1";
  const appendRequestIdRef = useRef(
    globalThis.crypto?.randomUUID?.() ||
      `invoice-append-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isQuoteVerified, setIsQuoteVerified] = useState(null); // null = unknown, true/false = verified status
  const [selectedOrder, setSelectedOrder] = useState(null); // Selected order/job for invoice
  const [linkedOrderId, setLinkedOrderId] = useState(null); // Preselected order/job id for edit mode
  const [paidAmount, setPaidAmount] = useState(0); // Paid amount from quote deposit
  const [createProductModal, setCreateProductModal] = useState({
    open: false,
    index: null,
    name: "",
  });
  const isEditMode = !!invoiceId;

  const openCreateProductModal = (index, name) => {
    setCreateProductModal({ open: true, index, name });
  };

  const closeCreateProductModal = () => {
    setCreateProductModal({ open: false, index: null, name: "" });
  };

  // Helper function to get initial form state
  const getInitialFormData = (quote = null, invoice = null) => {
    if (invoice) {
      // Edit mode - populate from invoice
      return {
        customer: invoice.customerId
          ? {
              customerId: invoice.customerId,
              name: invoice.customerName || "",
              phone: invoice.customerPhone || "",
              email: "",
            }
          : null,
        items: (invoice.items || []).map((item) => ({
          product: null,
          productId: item.productId || null,
          description: item.description || "",
          quantity: item.quantity || 1,
          unitPrice: item.unitPrice ?? 0,
          total:
            item.total != null && item.total !== ""
              ? item.total
              : Number(item.unitPrice || 0) * Number(item.quantity || 1),
          supplier: item.supplier || "bubakii",
          isCustom: !item.productId,
          marginMode: item.marginMode || null,
          providerCostAmount:
            item.providerCostAmount != null && item.providerCostAmount !== ""
              ? item.providerCostAmount
              : "",
          platformMarginAmount:
            item.platformMarginAmount != null && item.platformMarginAmount !== ""
              ? item.platformMarginAmount
              : "",
          priceCardElementId: item.priceCardElementId || null,
          priceCardId: item.priceCardId || null,
          priceCardVersion: item.priceCardVersion ?? null,
          unit: item.unit || null,
        })) || [createEmptyLineItem()],
        taxRate: invoice.taxRate ?? 0,
        dueDate: invoice.dueDate
          ? new Date(invoice.dueDate).toISOString().split("T")[0]
          : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
              .toISOString()
              .split("T")[0],
        notes: invoice.notes || "",
        leadData: null,
      };
    }

    // Create mode - populate from quote if available
    // ✅ CRITICAL: Preserve item pricing 1:1 from quote (don't redistribute)
    const quoteItems = quote?.items?.map((item) => {
      const priced = resolveQuoteItemPricing(item, {
        quoteItemCount: quote.items?.length || 1,
        quoteTotal: quote?.estimatedCost || quote?.totalCost || 0,
      });

      return {
        product: null,
        productId: item.productId || null,
        description: item.description || "Item",
        quantity: priced.quantity,
        unitPrice: priced.unitPrice,
        total: priced.total,
        supplier: item.supplier || "bubakii",
        isCustom: !item.productId,
        marginMode: null,
        providerCostAmount: "",
        platformMarginAmount: "",
        priceCardElementId: item.priceCardElementId || null,
        priceCardId: item.priceCardId || null,
        priceCardVersion: item.priceCardVersion ?? null,
        unit: item.unit || null,
      };
    });

    return {
      customer: quote
        ? {
            customerId: quote.customerId,
            name: quote.customerName || "",
            phone: quote.customerPhone || "",
            email: "",
          }
        : null,
      items: quoteItems && quoteItems.length > 0 ? quoteItems : [createEmptyLineItem()],
      taxRate: 0, // No VAT by default - admin opts in per invoice via the Tax Rate field
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      notes: quote ? `Converted from quote ${quote.id || quote._id}` : "",
      // Optional lead data
      leadData: quote
        ? {
            from: quote.from,
            to: quote.to,
            date: quote.date,
            items: quote.items,
          }
        : null,
      // Store paid amount info if available from quote
      paidAmount: quote?.paidAmount || 0,
      depositAmount: quote?.depositAmount || 0,
    };
  };

  const [formData, setFormData] = useState(getInitialFormData(quoteData));

  // Load invoice data if in edit mode
  useEffect(() => {
    if (!isEditMode) return;
    const run = async () => {
      try {
        setLoadingData(true);
        const result = await getInvoice(invoiceId);
        if (result.success && result.data) {
          const invoiceData = result.data;
          const nextFormData = getInitialFormData(null, invoiceData);
          setFormData(
            isAppendMode
              ? { ...nextFormData, items: [createEmptyLineItem()] }
              : nextFormData
          );
          setLinkedOrderId(invoiceData.jobId || null);
          
        } else {
          setError(result.error || "Failed to load invoice");
        }
      } catch (err) {
        setError(err.message || "Failed to load invoice");
      } finally {
        setLoadingData(false);
      }
    };
    run();
  }, [isEditMode, invoiceId, isAppendMode]);

  // Load quote data if quoteId query param is present
  useEffect(() => {
    if (!quoteIdParam || isEditMode) {
      setIsQuoteVerified(null); // Reset when not loading from quote
      return;
    }
    const run = async () => {
      try {
        setLoadingData(true);
        const result = await loadQuoteForInvoice(quoteIdParam);
        if (result.success && result.data) {
          setFormData(getInitialFormData(result.data));
          // Store quote verification status
          setIsQuoteVerified(result.data.isQuoteVerified || false);
          // Store paid amount from quote deposit
          setPaidAmount(result.data.paidAmount || result.data.depositAmount || 0);

          // Show error if quote is not verified
          if (!result.data.isQuoteVerified) {
            setError(
              "Quote must be verified (deposit_verified) before creating an invoice"
            );
          }
        } else {
          setError(result.error || "Failed to load quote");
          setIsQuoteVerified(false);
          setPaidAmount(0);
        }
      } catch (err) {
        setError(err.message || "Failed to load quote");
        setIsQuoteVerified(false);
      } finally {
        setLoadingData(false);
      }
    };
    run();
  }, [quoteIdParam, isEditMode]);

  const handleChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.value,
    });
    setError(null);
  };

  const handleItemChange = (index, field) => (e) => {
    const value = e.target ? e.target.value : e;

    setFormData((prev) => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        [field]: value,
      };

      if (field === "quantity" || field === "unitPrice") {
        const quantity = Number(newItems[index].quantity ?? 1);
        const unitPrice =
          newItems[index].unitPrice === "" ||
          newItems[index].unitPrice === null ||
          newItems[index].unitPrice === undefined
            ? 0
            : Number(newItems[index].unitPrice ?? 0);
        newItems[index].total = Math.round(quantity * unitPrice * 100) / 100;
        newItems[index].marginMode = null;
        newItems[index].providerCostAmount = "";
        newItems[index].platformMarginAmount = "";
      }

      return { ...prev, items: newItems };
    });
    setError(null);
  };

  const handleEconomicsChange = (index, field) => (event) => {
    const rawValue = event.target.value;
    setFormData((prev) => {
      const items = [...prev.items];
      const item = items[index];
      if (rawValue === "") {
        items[index] = {
          ...item,
          providerCostAmount: "",
          platformMarginAmount: "",
          marginMode: null,
        };
        return { ...prev, items };
      }

      const amount = Number(rawValue);
      const total = Number(item.total || 0);
      const otherAmount = Math.round((total - amount) * 100) / 100;
      items[index] = {
        ...item,
        marginMode: "FIXED_AMOUNT",
        [field]: rawValue,
        [field === "providerCostAmount"
          ? "platformMarginAmount"
          : "providerCostAmount"]: otherAmount >= 0 ? otherAmount : "",
      };
      return { ...prev, items };
    });
    setError(null);
  };

  // Handle product selection or clear (null)
  const handleProductSelect = (index) => (product) => {
    setFormData((prev) => {
      const newItems = [...prev.items];
      const currentItem = newItems[index];

      if (!product) {
        newItems[index] = {
          ...clearPriceCardSnapshot(currentItem),
          product: null,
          productId: null,
          isCustom: true,
        };
        return { ...prev, items: newItems };
      }

      const desc =
        currentItem.description && String(currentItem.description).trim()
          ? currentItem.description
          : product.name || "";

      const keepPrice =
        currentItem.unitPrice !== "" &&
        currentItem.unitPrice !== null &&
        currentItem.unitPrice !== undefined;
      const effectiveUnitPrice = keepPrice
        ? currentItem.unitPrice
        : product.unitPrice ?? "";

      const keepSupplier =
        currentItem.supplier &&
        currentItem.supplier !== "bubakii";
      const supplier = keepSupplier
        ? currentItem.supplier
        : product.defaultSupplier ?? currentItem.supplier ?? "bubakii";

      const quantity = Number(currentItem.quantity ?? 1);
      const upNum =
        effectiveUnitPrice === "" ? 0 : Number(effectiveUnitPrice ?? 0);

      const newTotal = Math.round(quantity * upNum * 100) / 100;
      const prevTotal = Number(currentItem.total || 0);
      const totalChanged = Math.abs(prevTotal - newTotal) > 0.01;
      const marginPatch = totalChanged
        ? { marginMode: null, providerCostAmount: "", platformMarginAmount: "" }
        : {
            marginMode: currentItem.marginMode ?? null,
            providerCostAmount: currentItem.providerCostAmount ?? "",
            platformMarginAmount: currentItem.platformMarginAmount ?? "",
          };

      newItems[index] = {
        ...clearPriceCardSnapshot(currentItem),
        product,
        productId: product.id || null,
        description: desc,
        unitPrice: effectiveUnitPrice,
        isCustom: false,
        supplier,
        total: newTotal,
        ...marginPatch,
      };

      return { ...prev, items: newItems };
    });
  };

  const handleCustomItem = (index) => (description) => {
    setFormData((prev) => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...clearPriceCardSnapshot(newItems[index]),
        product: null,
        productId: null,
        description,
        isCustom: true,
        persistProduct: true,
        marginMode: null,
        providerCostAmount: "",
        platformMarginAmount: "",
      };
      return { ...prev, items: newItems };
    });
  };

  const replaceLineItem = (index) => (lineItem) => {
    setFormData((prev) => {
      const items = [...prev.items];
      items[index] = lineItem;
      return { ...prev, items };
    });
    setError(null);
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, createEmptyLineItem()],
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData((prev) => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index),
      }));
    }
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce(
      (sum, item) => sum + (item.total || 0),
      0
    );
    const tax = Math.round(subtotal * (formData.taxRate / 100));
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Validate required fields
    if (!formData.customer) {
      setError("Customer is required");
      setLoading(false);
      return;
    }

    // Validate line items if provided (line items are optional)
    const validItems = formData.items.filter(
      (item) => item.description && item.description.trim().length > 0
    );
    if (
      validItems.some(
        (item) =>
          item.pricingOverride?.applied === true &&
          !item.pricingOverride?.reason?.trim(),
      )
    ) {
      setError("A reason is required for every price-card override");
      setLoading(false);
      return;
    }

    // If items are provided, validate they have required fields
    if (formData.items && formData.items.length > 0) {
      // Validate all items have supplier
      const itemsWithoutSupplier = validItems.filter((item) => !item.supplier);
      if (itemsWithoutSupplier.length > 0) {
        setError("All line items must have a supplier selected");
        setLoading(false);
        return;
      }

      // Validate unitPrice: allow 0 (free), but reject blank when product is selected
      const itemsWithInvalidPrice = validItems.filter((item) => {
        // Blank is invalid if product is selected (should use product default)
        if (
          item.productId &&
          (item.unitPrice === "" ||
            item.unitPrice === null ||
            item.unitPrice === undefined)
        ) {
          return true;
        }
        // Negative prices are invalid
        if (
          item.unitPrice !== "" &&
          item.unitPrice !== null &&
          item.unitPrice !== undefined &&
          Number(item.unitPrice) < 0
        ) {
          return true;
        }
        return false;
      });

      if (itemsWithInvalidPrice.length > 0) {
        setError(
          "All items must have a valid unit price (0 is allowed for free items)"
        );
        setLoading(false);
        return;
      }
    }

    // Validate required fields
    if (!formData.customer?.customerId && !formData.customer?._id) {
      setError("Customer is required. Please select a customer.");
      setLoading(false);
      return;
    }

    // Resolve jobId from URL param or selected order (in edit mode, allow selectedOrder to override)
    const resolvedJobId = isEditMode
      ? selectedOrder?.jobId || selectedOrder?.id || jobIdParam || null
      : jobIdParam || selectedOrder?.jobId || selectedOrder?.id || null;

    if (!isEditMode && !resolvedJobId) {
      setError(
        "Order / job is required. Please link an order before creating an invoice."
      );
      setLoading(false);
      return;
    }

    const { subtotal, tax, total } = calculateTotals();

    const invoiceData = {
      customerId: formData.customer.customerId || formData.customer._id || null,
      customerName: formData.customer.name,
      customerPhone: formData.customer.phone || null,
      customerEmail: formData.customer.email || null,
      jobId: resolvedJobId,
      items: formData.items.map((item) => {
        const row = {
          description: item.description,
          quantity: Number(item.quantity),
          unitPrice: Number(item.unitPrice),
          total: Number(item.total),
          productId: item.productId || null,
          isCustom: !item.productId,
          supplier: item.supplier || "bubakii",
          persistProduct: item.persistProduct || false,
          priceCardElementId: item.priceCardElementId || null,
          priceCardId: item.priceCardId || null,
          priceCardVersion: item.priceCardVersion ?? null,
          unit: item.unit || null,
          pricingOverride: item.pricingOverride || undefined,
        };
        const pcRaw = item.providerCostAmount;
        const pmRaw = item.platformMarginAmount;
        const hasPc =
          pcRaw !== "" && pcRaw != null && Number.isFinite(Number(pcRaw));
        const hasPm =
          pmRaw !== "" && pmRaw != null && Number.isFinite(Number(pmRaw));
        if (hasPc || hasPm) {
          row.marginMode = "FIXED_AMOUNT";
          if (hasPc) row.providerCostAmount = Number(pcRaw);
          if (hasPm) row.platformMarginAmount = Number(pmRaw);
        }
        return row;
      }),
      subtotal,
      taxRate: Number(formData.taxRate),
      tax,
      total,
      dueDate: formData.dueDate,
      notes: formData.notes || "",
      quoteId: formData.quoteId || quoteIdParam || quoteData?.id || null,
      leadData: formData.leadData,
    };

    try {
      let response;
      if (isAppendMode) {
        response = await appendInvoiceItems(
          invoiceId,
          invoiceData.items,
          appendRequestIdRef.current
        );
      } else if (isEditMode) {
        response = await updateInvoice(invoiceId, invoiceData);
      } else {
        response = await createInvoice(invoiceData);
      }

      if (response.success) {
        const createdInvoiceId = response.invoiceId || response.data?.id;
        setSuccess(true);
        if (!isEditMode) {
          // Clear form after successful save (only for create mode)
          handleReset();
        }
        setTimeout(() => {
          const targetInvoiceId = isEditMode ? invoiceId : createdInvoiceId;
          const targetPath = `/invoices/${targetInvoiceId}`;
          navigate(targetPath);
        }, 1000);
      } else {
        setError(
          response.error ||
            `Failed to ${isEditMode ? "update" : "create"} invoice`
        );
      }
    } catch (err) {
      setError(
        err.message || `Failed to ${isEditMode ? "update" : "create"} invoice`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      customer: null,
      items: [createEmptyLineItem()],
      taxRate: 0, // No VAT by default - admin opts in per invoice via the Tax Rate field
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      notes: "",
      leadData: null,
    });
    setSelectedOrder(null);
    setError(null);
    setSuccess(false);
  };

  const { subtotal, tax, total } = calculateTotals();

  if (loadingData) {
    return (
      <Box sx={{ maxWidth: 1200, mx: "auto", p: 3, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>
          {isEditMode ? "Loading invoice..." : "Loading quote data..."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/invoices")}
          variant="outlined"
        >
          Back to Invoices
        </Button>
        <Typography variant="h4">
          {isAppendMode
            ? "Add Charges to Invoice"
            : isEditMode
            ? "Edit Invoice"
            : quoteData || quoteIdParam
              ? "Create Invoice from Quote"
              : "Create New Invoice"}
        </Typography>
      </Box>

      <Card>
        <CardContent>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Invoice {isAppendMode ? "charges added" : isEditMode ? "updated" : "created"} successfully!
              Redirecting...
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Order/Job Selection - Required for invoice creation, editable in edit mode */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Order / Job Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                {!isEditMode && !jobIdParam && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This invoice must be linked to an Order/Job. Search for
                    an existing order or create one from the Orders page.
                  </Alert>
                )}
                {isEditMode && !isAppendMode && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    You can change the linked Order/Job for this invoice.
                  </Alert>
                )}
                <OrderSearchAutocomplete
                  value={selectedOrder}
                  selectedOrderId={isEditMode ? linkedOrderId : jobIdParam}
                  minChars={2}
                  restrictToCustomer={false}
                  onChange={(order) => {
                    setSelectedOrder(order);
                    setError(null);

                    // ✅ Always populate customer from order (even if already set, update if different)
                    if (order?.customerId) {
                      setFormData((prev) => {
                        const prevCustomerId =
                          prev.customer?.customerId || prev.customer?._id || null;

                        // If customer is missing OR doesn't match the order's customer, set it from the order
                        if (!prevCustomerId || prevCustomerId !== order.customerId) {
                          return {
                            ...prev,
                            customer: {
                              customerId: order.customerId,
                              name: order.customer || order.customerName || "",
                              phone: order.customerPhone || "",
                              email: "",
                            },
                          };
                        }

                        return prev;
                      });
                    }
                  }}
                  customerId={
                    formData.customer?.customerId ||
                    formData.customer?._id ||
                    customerIdParam ||
                    null
                  }
                  label={
                    isAppendMode
                      ? "Order / Job"
                      : isEditMode
                        ? "Order / Job (Editable)"
                        : "Order / Job (Required)"
                  }
                  disabled={isAppendMode || (!isEditMode && !!jobIdParam)}
                />
                {jobIdParam && selectedOrder && !isEditMode && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Linked to: {selectedOrder.id}
                  </Typography>
                )}
              </Grid>

              {/* Customer Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <CustomerSearchAutocomplete
                  value={formData.customer}
                  onChange={(customer) => {
                    setFormData({
                      ...formData,
                      customer: customer,
                    });
                    setError(null);
                  }}
                  label="Customer *"
                  required
                  disabled={isAppendMode}
                  error={
                    error && !formData.customer ? "Customer is required" : null
                  }
                />
              </Grid>

              {/* Invoice Items */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Invoice Items
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              {formData.items.map((item, index) => (
                <React.Fragment key={index}>
                  <Grid item xs={12} md={3}>
                    <PriceCardLineItemEditor
                      lineItem={item}
                      onLineItemChange={replaceLineItem(index)}
                      includeProducts={false}
                      onMissingTariff={onMissingTariff}
                      label="Price card or custom item *"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Description *"
                      value={item.description}
                      onChange={handleItemChange(index, "description")}
                      required
                      placeholder="Item description"
                    />
                  </Grid>
                  <Grid item xs={12} md={1.5}>
                    <TextField
                      fullWidth
                      label="Quantity *"
                      type="number"
                      value={item.quantity}
                      onChange={handleItemChange(index, "quantity")}
                      required
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={1.5}>
                    <TextField
                      fullWidth
                      label="Unit Price (R)"
                      type="number"
                      value={item.unitPrice}
                      onChange={handleItemChange(index, "unitPrice")}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <SupplierSelect
                      value={item.supplier}
                      onChange={(supplier) => {
                        handleItemChange(index, "supplier")(supplier);
                      }}
                      defaultSupplier={item.product?.defaultSupplier}
                      label="Partner / Supplier *"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <TextField
                      fullWidth
                      label="Total"
                      value={item.total.toFixed(2)}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <IconButton
                      onClick={() => removeItem(index)}
                      disabled={formData.items.length === 1}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        alignItems: "flex-start",
                        pl: { xs: 0, md: 0 },
                      }}
                    >
                      <TextField
                        size="small"
                        label="Provider cost (internal)"
                        type="number"
                        value={item.providerCostAmount}
                        onChange={handleEconomicsChange(
                          index,
                          "providerCostAmount"
                        )}
                        inputProps={{ min: 0, step: 0.01 }}
                        helperText="Partner payout; margin is calculated"
                      />
                      <TextField
                        size="small"
                        label="Platform margin (internal)"
                        type="number"
                        value={item.platformMarginAmount}
                        onChange={handleEconomicsChange(
                          index,
                          "platformMarginAmount"
                        )}
                        inputProps={{ min: 0, step: 0.01 }}
                        helperText="Internal only; partner payout is calculated"
                      />
                    </Box>
                  </Grid>
                </React.Fragment>
              ))}

              <CreateProductModal
                open={createProductModal.open}
                onClose={closeCreateProductModal}
                title="Create Product from Line Item"
                initialValues={{
                  name: createProductModal.name,
                  description: createProductModal.name,
                  unitPrice:
                    createProductModal.index !== null
                      ? Number(
                          formData.items[createProductModal.index]?.unitPrice ||
                            0
                        )
                      : 0,
                  defaultSupplier:
                    createProductModal.index !== null
                      ? formData.items[createProductModal.index]?.supplier ||
                        "bubakii"
                      : "bubakii",
                  category: "services",
                  active: true,
                }}
                onUseAsOneOff={() => {
                  if (createProductModal.index === null) return;
                  handleCustomItem(createProductModal.index)(
                    createProductModal.name
                  );
                }}
                onCreated={(product) => {
                  if (createProductModal.index === null) return;
                  handleProductSelect(createProductModal.index)(product);
                }}
              />

              <Grid item xs={12}>
                <Button
                  startIcon={<Add />}
                  onClick={addItem}
                  variant="outlined"
                  size="small"
                >
                  Add Item
                </Button>
              </Grid>

              {/* Pricing Summary */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {isAppendMode ? "New charge summary" : "Pricing"}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Subtotal"
                  value={subtotal.toFixed(2)}
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Tax Rate (%)"
                  type="number"
                  value={formData.taxRate}
                  onChange={handleChange("taxRate")}
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Tax Amount"
                  value={tax.toFixed(2)}
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Total"
                  value={total.toFixed(2)}
                  disabled
                  sx={{ fontWeight: "bold" }}
                />
              </Grid>

              {/* Paid Amount - Show when loading from quote with deposit */}
              {paidAmount > 0 && (
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Paid Amount (Deposit)"
                    value={paidAmount.toFixed(2)}
                    disabled
                    sx={{
                      fontWeight: "bold",
                      "& .MuiInputBase-input": {
                        color: "success.main",
                      },
                    }}
                    helperText="Deposit from quote will be added as payment after invoice creation"
                  />
                </Grid>
              )}

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Due Date *"
                  type="date"
                  value={formData.dueDate}
                  onChange={handleChange("dueDate")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={handleChange("notes")}
                  multiline
                  rows={4}
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/invoices")}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    disabled={loading}
                    startIcon={<Refresh />}
                  >
                    Reset Form
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={
                      loading ? <CircularProgress size={20} /> : <Save />
                    }
                    disabled={
                      loading ||
                      (quoteIdParam && !isEditMode && isQuoteVerified === false)
                    }
                    title={
                      quoteIdParam && !isEditMode && isQuoteVerified === false
                        ? "Quote must be verified (deposit_verified) before creating an invoice"
                        : ""
                    }
                  >
                    {isAppendMode
                      ? "Add Charges"
                      : isEditMode
                        ? "Update Invoice"
                        : "Create Invoice"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default InvoiceForm;
